import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { localSafetyFormService } from '../services/localDataService';
import { safetyFormService } from '../services/amplifyDataService';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { toast } from 'react-toastify';
import './Screen.css';
import type { SafetyForm as SafetyFormType } from '../models/index.js';
// runtime model imports are not needed directly in this UI file; the
// `safetyFormService` handles DataStore operations. Keep only type import.

export default function PTPCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const isOnline = useNetworkStatus();

  const [formData, setFormData] = useState<Partial<SafetyFormType>>({
    formType: 'PTP',
    projectName: '',
    location: '',
    workDate: new Date().toISOString().split('T')[0],
    foremanName: '',
    requiredPermits: [],
    requiredChecklist: [],
    ppe: '',
    hazards: [],
    controlMeasures: [],
    tasks: [],
    emergencyActionPlan: false,
    emergencyMusterArea: '',
    emergencyContacts: '',
    teamMembers: [],
    status: 'draft',
  });

  const [foremanSignature, setForemanSignature] = useState<SignatureCanvas | null>(null);
  const [newHazard, setNewHazard] = useState('');
  const [newControlMeasure, setNewControlMeasure] = useState('');
  const [newTask, setNewTask] = useState('');
  const [newTeamMember, setNewTeamMember] = useState('');
  const [selectedPermits, setSelectedPermits] = useState<string[]>([]);
  const [selectedChecklist, setSelectedChecklist] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const permitOptions = [
    'Hot Work Permit',
    'Confined Space Entry',
    'Lockout/Tagout',
    'Excavation Permit',
    'Crane Operation',
    'Electrical Work',
    'Working at Heights',
  ];

  const checklistOptions = [
    'Toolbox Talk Completed',
    'Site Inspection Done',
    'Equipment Checked',
    'Safety Briefing Completed',
    'Emergency Procedures Reviewed',
  ];

  useEffect(() => {
    if (isEdit && id) {
      loadForm(id);
    }
  }, [id, isEdit]);

  const loadForm = async (formId: string) => {
    try {
      // Try Amplify DataStore first (Gen 1)
      try {
        const form = await safetyFormService.getById(formId);
        if (form) {
          setFormData(form);
          setSelectedPermits((form.requiredPermits || []).filter((s): s is string => s != null));
          setSelectedChecklist((form.requiredChecklist || []).filter((s): s is string => s != null));
          return;
        }
      } catch (amplifyError) {
        // If Amplify fails, fall back to local service
        console.log('Amplify DataStore not available, using local service');
      }
      
      // Fallback to local service
      const form = await localSafetyFormService.getById(formId);
      if (form) {
        setFormData(form as SafetyFormType);
        setSelectedPermits(form.requiredPermits || []);
        setSelectedChecklist(form.requiredChecklist || []);
      }
    } catch (error) {
      console.error('Failed to load form:', error);
      toast.error('Failed to load form');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const signatureData = foremanSignature?.toDataURL() || '';
      
      if (isEdit && id) {
        const original = await safetyFormService.getById(id);
        if (!original) {
          throw new Error("Form not found");
        }
        await safetyFormService.update(id, {
          ...formData,
          foremanSignature: signatureData,
          requiredPermits: selectedPermits,
          requiredChecklist: selectedChecklist,
        });
        toast.success('PTP updated successfully');
      } else {
        await safetyFormService.create({
          ...formData,
          foremanSignature: signatureData,
          requiredPermits: selectedPermits,
          requiredChecklist: selectedChecklist,
        });
        toast.success('PTP created successfully');
      }

      navigate('/ptp');
    } catch (error: any) {
      console.error('Failed to save form:', error);
      toast.error(`Failed to save form: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const addHazard = () => {
    if (newHazard.trim()) {
      setFormData({
        ...formData,
        hazards: [...(formData.hazards || []), newHazard.trim()],
      });
      setNewHazard('');
    }
  };

  const removeHazard = (index: number) => {
    const hazards = [...(formData.hazards || [])];
    hazards.splice(index, 1);
    setFormData({ ...formData, hazards });
  };

  const addControlMeasure = () => {
    if (newControlMeasure.trim()) {
      setFormData({
        ...formData,
        controlMeasures: [...(formData.controlMeasures || []), newControlMeasure.trim()],
      });
      setNewControlMeasure('');
    }
  };

  const removeControlMeasure = (index: number) => {
    const measures = [...(formData.controlMeasures || [])];
    measures.splice(index, 1);
    setFormData({ ...formData, controlMeasures: measures });
  };

  const addTask = () => {
    if (newTask.trim()) {
      setFormData({
        ...formData,
        tasks: [...(formData.tasks || []), newTask.trim()],
      });
      setNewTask('');
    }
  };

  const removeTask = (index: number) => {
    const tasks = [...(formData.tasks || [])];
    tasks.splice(index, 1);
    setFormData({ ...formData, tasks });
  };

  const addTeamMember = () => {
    if (newTeamMember.trim()) {
      setFormData({
        ...formData,
        teamMembers: [...(formData.teamMembers || []), newTeamMember.trim()],
      });
      setNewTeamMember('');
    }
  };

  const removeTeamMember = (index: number) => {
    const members = [...(formData.teamMembers || [])];
    members.splice(index, 1);
    setFormData({ ...formData, teamMembers: members });
  };

  const togglePermit = (permit: string) => {
    setSelectedPermits((prev) =>
      prev.includes(permit) ? prev.filter((p) => p !== permit) : [...prev, permit]
    );
  };

  const toggleChecklist = (item: string) => {
    setSelectedChecklist((prev) =>
      prev.includes(item) ? prev.filter((c) => c !== item) : [...prev, item]
    );
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>{isEdit ? 'Edit' : 'Create'} Pre-Task Planning (PTP) Form</h1>
        <p className="screen-subtitle">
          {isEdit ? 'Update PTP form details' : 'Complete all sections to create a new PTP'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="ptp-form">
        {/* Basic Information */}
        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Project Name *</label>
              <input
                type="text"
                value={formData.projectName || ''}
                onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Work Date *</label>
              <input
                type="date"
                value={formData.workDate || ''}
                onChange={(e) => setFormData({ ...formData, workDate: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Foreman Name *</label>
              <input
                type="text"
                value={formData.foremanName || ''}
                onChange={(e) => setFormData({ ...formData, foremanName: e.target.value })}
                required
              />
            </div>
          </div>
        </div>

        {/* Required Permits */}
        <div className="form-section">
          <h2>Required Permits</h2>
          <div className="checkbox-group">
            {permitOptions.map((permit) => (
              <label key={permit} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedPermits.includes(permit)}
                  onChange={() => togglePermit(permit)}
                />
                <span>{permit}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Required Checklist */}
        <div className="form-section">
          <h2>Required Checklist</h2>
          <div className="checkbox-group">
            {checklistOptions.map((item) => (
              <label key={item} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedChecklist.includes(item)}
                  onChange={() => toggleChecklist(item)}
                />
                <span>{item}</span>
              </label>
            ))}
          </div>
        </div>

        {/* PPE */}
        <div className="form-section">
          <h2>Personal Protective Equipment (PPE)</h2>
          <div className="form-group">
            <label>PPE Requirements</label>
            <textarea
              value={formData.ppe || ''}
              onChange={(e) => setFormData({ ...formData, ppe: e.target.value })}
              rows={4}
              placeholder="List all required PPE (e.g., Hard hat, Safety glasses, Steel-toed boots, etc.)"
            />
          </div>
        </div>

        {/* Hazards */}
        <div className="form-section">
          <h2>Identified Hazards</h2>
          <div className="form-group">
            <div className="input-group">
              <input
                type="text"
                value={newHazard}
                onChange={(e) => setNewHazard(e.target.value)}
                placeholder="Enter hazard description"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHazard())}
              />
              <button type="button" onClick={addHazard} className="btn btn-secondary">
                Add
              </button>
            </div>
          </div>
          <div className="list-items">
            {formData.hazards?.map((hazard, index) => (
              <div key={index} className="list-item">
                <span>{hazard}</span>
                <button
                  type="button"
                  onClick={() => removeHazard(index)}
                  className="btn btn-sm btn-danger"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Control Measures */}
        <div className="form-section">
          <h2>Control Measures</h2>
          <div className="form-group">
            <div className="input-group">
              <input
                type="text"
                value={newControlMeasure}
                onChange={(e) => setNewControlMeasure(e.target.value)}
                placeholder="Enter control measure"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addControlMeasure())}
              />
              <button type="button" onClick={addControlMeasure} className="btn btn-secondary">
                Add
              </button>
            </div>
          </div>
          <div className="list-items">
            {formData.controlMeasures?.map((measure, index) => (
              <div key={index} className="list-item">
                <span>{measure}</span>
                <button
                  type="button"
                  onClick={() => removeControlMeasure(index)}
                  className="btn btn-sm btn-danger"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Tasks */}
        <div className="form-section">
          <h2>Tasks</h2>
          <div className="form-group">
            <div className="input-group">
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="Enter task description"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTask())}
              />
              <button type="button" onClick={addTask} className="btn btn-secondary">
                Add
              </button>
            </div>
          </div>
          <div className="list-items">
            {formData.tasks?.map((task, index) => (
              <div key={index} className="list-item">
                <span>{task}</span>
                <button
                  type="button"
                  onClick={() => removeTask(index)}
                  className="btn btn-sm btn-danger"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Team Members */}
        <div className="form-section">
          <h2>Team Members</h2>
          <div className="form-group">
            <div className="input-group">
              <input
                type="text"
                value={newTeamMember}
                onChange={(e) => setNewTeamMember(e.target.value)}
                placeholder="Enter team member name"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTeamMember())}
              />
              <button type="button" onClick={addTeamMember} className="btn btn-secondary">
                Add
              </button>
            </div>
          </div>
          <div className="list-items">
            {formData.teamMembers?.map((member, index) => (
              <div key={index} className="list-item">
                <span>{member}</span>
                <button
                  type="button"
                  onClick={() => removeTeamMember(index)}
                  className="btn btn-sm btn-danger"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Information */}
        <div className="form-section">
          <h2>Emergency Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.emergencyActionPlan || false}
                  onChange={(e) =>
                    setFormData({ ...formData, emergencyActionPlan: e.target.checked })
                  }
                />
                Emergency Action Plan Reviewed
              </label>
            </div>
            <div className="form-group">
              <label>Emergency Muster Area</label>
              <input
                type="text"
                value={formData.emergencyMusterArea || ''}
                onChange={(e) =>
                  setFormData({ ...formData, emergencyMusterArea: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Emergency Contacts</label>
              <textarea
                value={formData.emergencyContacts || ''}
                onChange={(e) => setFormData({ ...formData, emergencyContacts: e.target.value })}
                rows={3}
                placeholder="Emergency contact information"
              />
            </div>
          </div>
        </div>

        {/* Foreman Signature */}
        <div className="form-section">
          <h2>Foreman Signature</h2>
          <div className="form-group">
            <label>Foreman Signature *</label>
            <div className="signature-container">
              <SignatureCanvas
                ref={(ref: any) => setForemanSignature(ref)}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                }}
              />
              <div className="signature-actions">
                <button
                  type="button"
                  onClick={() => foremanSignature?.clear()}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/ptp')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : isEdit ? 'Update PTP' : 'Save & Submit PTP'}
          </button>
        </div>

        {!isOnline && (
          <div className="offline-notice">
            ⚠️ You are offline. Form will be saved locally and synced when online.
          </div>
        )}
      </form>
    </div>
  );
}
